import type { ReactNode } from "react";
import { AlertTriangle, CheckCircle2, Info, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";

// Each tone has its own icon, so the state never rests on colour alone.
const TONES = {
  error: {
    box: "border-destructive/30 bg-destructive/[0.06]",
    icon: XCircle,
    iconTone: "text-destructive",
    title: "text-destructive",
  },
  warning: {
    box: "border-warning/50 bg-warning/[0.09]",
    icon: AlertTriangle,
    iconTone: "text-warning-foreground",
    title: "text-foreground",
  },
  success: {
    box: "border-success/30 bg-success/[0.07]",
    icon: CheckCircle2,
    iconTone: "text-success",
    title: "text-success",
  },
  info: {
    box: "border-secondary/30 bg-accent",
    icon: Info,
    iconTone: "text-brand-600",
    title: "text-accent-foreground",
  },
} as const;

type AlertProps = {
  variant?: keyof typeof TONES;
  title?: string;
  children?: ReactNode;
  action?: ReactNode;
};

export function Alert({ variant = "info", title, children, action }: AlertProps) {
  const urgent = variant === "error" || variant === "warning";
  const tone = TONES[variant];
  const Icon = tone.icon;
  return (
    <div
      role={urgent ? "alert" : "status"}
      className={cn(
        "flex flex-col gap-3 rounded-lg border px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between",
        tone.box,
      )}
    >
      <div className="flex min-w-0 gap-3">
        <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", tone.iconTone)} aria-hidden />
        <div className="min-w-0">
          {title && <p className={cn("font-semibold", tone.title)}>{title}</p>}
          {children && (
            <div className={title ? "mt-0.5 text-foreground/85" : tone.title}>{children}</div>
          )}
        </div>
      </div>
      {action && <div className="shrink-0 pl-7 sm:pl-0">{action}</div>}
    </div>
  );
}
